import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureModal from './SignatureModal';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  isGenderField,
  isMaritalStatusField,
} from '../constants/formOptions';
import './InteractiveForm.css';

const INPUT_TYPE_MAP = {
  email: 'email',
  phone: 'tel',
  date: 'date',
};

/**
 * Manual entry form: input fields for all form fields. On blur/save, calls onFieldUpdate
 * so the parent can update the backend and refresh progress/collected/pending.
 */
function InteractiveForm({ formType, fields, values, onFieldUpdate }) {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(true);

  useEffect(() => {
    setFormState(values || {});
  }, [values]);

  const getFieldLabel = (field) => {
    const key = `fields.${field}`;
    const translation = t(key);
    return translation !== key ? translation : field.replace(/_/g, ' ');
  };

  const allFieldNames = useMemo(() => {
    if (!fields || fields.length === 0) return [];
    return fields.map((f) => (typeof f === 'string' ? f : f?.name)).filter(Boolean);
  }, [fields]);

  const fieldMeta = useMemo(() => {
    const meta = {};
    (fields || []).forEach((f) => {
      if (f && typeof f === 'object' && f.name) meta[f.name] = f;
    });
    return meta;
  }, [fields]);

  const sections = useMemo(() => {
    const base = formType === 'REPRESENTATIVE'
      ? [
          { title: t('form.sectionApplicantDetails'), fields: ['nombre_representado', 'primer_apellido_representado', 'segundo_apellido_representado', 'nacionalidad_representado', 'numero_nie_representado', 'numero_pasaporte_representado', 'fecha_nacimiento_representado', 'lugar_nacimiento_representado', 'pais_nacimiento_representado', 'nombre_padre_representado', 'nombre_madre_representado', 'estado_civil_representado', 'domicilio_representado', 'numero_domicilio_representado', 'localidad_representado', 'codigo_postal_representado', 'provincia_representado', 'telefono_representado', 'email_representado'] },
          { title: t('form.sectionAuthorization'), fields: ['tipo_tramite'] },
          { title: t('form.sectionRepresentativeDetails'), fields: ['documento_representante', 'empresa_representante', 'nombre_representante', 'primer_apellido_representante', 'segundo_apellido_representante', 'domicilio_representante', 'numero_domicilio_representante', 'localidad_representante', 'codigo_postal_representante', 'provincia_representante', 'telefono_representante', 'email_representante'] },
          { title: t('form.sectionNotifications'), fields: ['notificaciones_electronicas'] },
          { title: t('form.sectionSignature'), fields: ['lugar_firma', 'fecha_firma', 'firma_solicitante'] },
        ]
      : [
          { title: t('form.sectionPersonalInfo'), fields: ['nombre', 'apellidos', 'fecha_nacimiento', 'nacionalidad', 'sexo', 'estado_civil'] },
          { title: t('form.sectionFamilyInfo'), fields: ['nombre_padre', 'nombre_madre'] },
          { title: t('form.sectionContactInfo'), fields: ['domicilio', 'localidad', 'provincia', 'codigo_postal', 'telefono', 'email'] },
          { title: t('form.sectionDocumentInfo'), fields: ['numero_pasaporte', 'numero_nie', 'fecha_entrada_espana', 'motivo_solicitud'] },
        ];

    const inBase = new Set(base.flatMap((s) => s.fields));
    const other = allFieldNames.filter((name) => !inBase.has(name));
    if (other.length > 0) {
      return [...base, { title: t('form.sectionOther'), fields: other }];
    }
    return base;
  }, [formType, t, allFieldNames]);

  const renderInput = (field) => {
    const value = formState[field] || '';
    const meta = fieldMeta[field] || {};

    if (field === 'firma_solicitante') {
      return (
        <div className="interactive-form-signature">
          {value && typeof value === 'string' && value.startsWith('data:image') ? (
            <img src={value} alt={t('dataPanel.signature')} className="interactive-form-signature-preview" />
          ) : (
            <div className="interactive-form-signature-placeholder">{t('dataPanel.addSignature')}</div>
          )}
          <button type="button" className="interactive-form-signature-btn" onClick={() => setShowSignatureModal(true)}>
            {value ? t('dataPanel.editSignature') : t('dataPanel.addSignature')}
          </button>
        </div>
      );
    }

    if (field === 'notificaciones_electronicas') {
      return (
        <label className="interactive-form-checkbox">
          <input
            type="checkbox"
            checked={value === 'yes' || value === true || value === 'true' || value === 'si' || value === 'sí'}
            onChange={(e) => {
              const next = e.target.checked ? 'yes' : 'no';
              setFormState((prev) => ({ ...prev, [field]: next }));
              onFieldUpdate(field, next, getFieldLabel(field));
            }}
          />
          <span>{t('form.allowElectronicNotifications')}</span>
        </label>
      );
    }

    if (meta.options && Array.isArray(meta.options) && meta.options.length > 0) {
      return (
        <select
          value={value || ''}
          onChange={(e) => {
            const next = e.target.value;
            setFormState((prev) => ({ ...prev, [field]: next }));
            onFieldUpdate(field, next, getFieldLabel(field));
          }}
          className="interactive-form-input interactive-form-select"
        >
          <option value="">—</option>
          {meta.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (isGenderField(field)) {
      return (
        <select
          value={value || ''}
          onChange={(e) => {
            const next = e.target.value;
            setFormState((prev) => ({ ...prev, [field]: next }));
            onFieldUpdate(field, next, getFieldLabel(field));
          }}
          className="interactive-form-input interactive-form-select"
        >
          <option value="">—</option>
          {GENDER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (isMaritalStatusField(field)) {
      return (
        <select
          value={value || ''}
          onChange={(e) => {
            const next = e.target.value;
            setFormState((prev) => ({ ...prev, [field]: next }));
            onFieldUpdate(field, next, getFieldLabel(field));
          }}
          className="interactive-form-input interactive-form-select"
        >
          <option value="">—</option>
          {MARITAL_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    const inputType = INPUT_TYPE_MAP[
      meta.type || (field.includes('email') ? 'email' : field.includes('telefono') ? 'phone' : 'text')
    ] || 'text';

    return (
      <input
        type={inputType}
        value={value}
        onChange={(e) => setFormState((prev) => ({ ...prev, [field]: e.target.value }))}
        onBlur={(e) => {
          const next = (e.target.value || '').trim();
          const current = (values?.[field] || '').toString().trim();
          if (next !== current) {
            onFieldUpdate(field, next, getFieldLabel(field));
          }
        }}
        placeholder={field.includes('fecha') ? 'DD/MM/YYYY' : getFieldLabel(field)}
        className="interactive-form-input"
      />
    );
  };

  const sectionsWithFields = sections
    .map((section) => ({
      ...section,
      visibleFields: section.fields.filter((field) =>
        allFieldNames.length === 0 || allFieldNames.includes(field)
      ),
    }))
    .filter((s) => s.visibleFields.length > 0);

  return (
    <div className="interactive-form">
      <div className="interactive-form-header">
        <div className="interactive-form-header-row">
          <h3>{t('form.title')}</h3>
          <button
            type="button"
            className="interactive-form-collapse-toggle"
            onClick={() => setIsFormOpen((prev) => !prev)}
          >
            {isFormOpen ? t('dataPanel.hideCollected') : t('dataPanel.showCollected')}
          </button>
        </div>
        {isFormOpen && <span className="interactive-form-hint">{t('form.syncHint')}</span>}
      </div>
      {isFormOpen && (
      <div className="interactive-form-body">
        {sectionsWithFields.length === 0 ? (
          <div className="interactive-form-empty">
            <p>{!fields || fields.length === 0 ? t('loading') + '...' : t('form.syncHint')}</p>
          </div>
        ) : (
          sectionsWithFields.map((section) => (
            <div key={section.title} className="interactive-form-section">
              <h4>{section.title}</h4>
              <div className="interactive-form-grid">
                {section.visibleFields.map((field) => (
                  <div key={field} className="interactive-form-field">
                    <label>{getFieldLabel(field)}</label>
                    {renderInput(field)}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      )}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={(dataUrl) => {
          setFormState((prev) => ({ ...prev, firma_solicitante: dataUrl }));
          onFieldUpdate('firma_solicitante', dataUrl, getFieldLabel('firma_solicitante'));
        }}
        initialDataUrl={values?.firma_solicitante}
      />
    </div>
  );
}

export default InteractiveForm;
